export const sanitizeUserInput = (input: string | number | undefined) => {
  return input?.toString()?.replace(/\s+/g, " ")?.trim();
};

/* eslint-disable no-redeclare */ // NOTE: linter no-redeclare fires on TypeScript function overloads (linter bug/misconfiguration)
/**
 * @see {@link sanitizeFileName}
 * @overload
 * @param fileName - The file name to sanitize.
 * @returns A sanitized file name, or an empty string if the input is undefined.
 */
export function sanitizeFileName(fileName: string | undefined): string;
/**
 * @see {@link sanitizeFileName}
 * @overload
 * @param fileName - The file name to sanitize.
 * @param limit - Maximum length of the sanitized file name (default is 255 characters).
 * @returns A sanitized file name, or an empty string if the input is undefined.
 */
export function sanitizeFileName(fileName: string | undefined, limit: number): string;
/**
 * @see {@link sanitizeFileName}
 * @overload
 * @param fileName - The file name to sanitize.
 * @param replacement - A string to replace invalid characters (default is an empty string).
 * @returns A sanitized file name, or an empty string if the input is undefined.
 */
export function sanitizeFileName(fileName: string | undefined, replacement: string): string;
/**
 * @see {@link sanitizeFileName}
 * @overload
 * @param fileName - The file name to sanitize.
 * @param replacement - A string to replace invalid characters (default is an empty string).
 * @param limit - Maximum length of the sanitized file name (default is 255 characters).
 * @returns A sanitized file name, or an empty string if the input is undefined.
 */
export function sanitizeFileName(fileName: string | undefined, replacement: string, limit: number): string;
/**
 * Sanitizes a file name (with or without extension) by removing invalid characters and trimming whitespace.
 *
 * Invalid characters include: / ? < > \ : * | "
 * Reserved filenames on Windows: CON, PRN, AUX, NUL, COM[0-9], LPT[0-9]
 * @see https://docs.microsoft.com/en-us/windows/win32/fileio/naming-a-file#naming-conventions
 *
 * Unicode Control characters: C0 0x00-0x1F, C1 0x80-0x9F
 * @see http://en.wikipedia.org/wiki/C0_and_C1_control_codes
 *
 * Reserved filenames on Unix-based systems: ".", ".."
 *
 * Remove multiple spaces and trim leading/trailing whitespace.
 *
 * Capped at 255 characters (or less) to avoid issues with file systems that have a maximum filename length.
 * @see https://docs.microsoft.com/en-us/windows/win32/fileio/naming-a-file#maximum-path-length-limitation
 * @see https://unix.stackexchange.com/questions/32795/what-is-the-maximum-allowed-filename-and-folder-size-with-ecryptfs
 *
 * @param fileName - The file name to sanitize.
 * @param limitOrReplacement - Either a string to replace invalid characters or a number for the maximum length of the sanitized file name.
 * @param limit - Maximum length of the sanitized file name (default is 255 characters).
 * @returns A sanitized file name, or an empty string if the input is undefined.
 */
export function sanitizeFileName(
  fileName: string | undefined,
  limitOrReplacement: number | string = "",
  limit: number = 255
): string {
  let replacement: string;
  if (typeof limitOrReplacement === "string") {
    replacement = limitOrReplacement;
  } else {
    replacement = "";
    limit = limitOrReplacement;
  }
  if (fileName === undefined || limit <= 0) return "";
  // Remove invalid characters
  fileName = fileName
    .replace(/[/?<>\\:*|"]/g, replacement)
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1F\x80-\x9F]/g, replacement)
    .replace(/\s+/g, " ")
    .trim();
  // Handle reserved filenames on Unix-based systems
  fileName = fileName.replace(/^\.+$/, replacement);
  // Handle reserved filenames on Windows
  fileName = fileName.replace(/^(con|prn|aux|nul|com\d|lpt\d)(\..*)?$/i, replacement);
  // Remove trailing spaces or periods (not allowed in Windows)
  fileName = replaceTrailingPeriodsAndSpaces(fileName, replacement);
  // Trim to max length, using Array.from to correctly handle multibyte characters (e.g. emoji)
  return Array.from(fileName.trim()).slice(0, limit).join("").trimEnd();
}

/**
 * Replaces trailing periods and spaces in a string with the specified replacement string.
 *
 * Doesn't use regex to avoid super-linear performance issues with certain patterns.
 * @param str
 * @param replacement
 */
function replaceTrailingPeriodsAndSpaces(str: string, replacement: string): string {
  let endIndex = str.length;

  // Find the index where trailing periods and spaces end
  while (endIndex > 0 && (str[endIndex - 1] === "." || str[endIndex - 1] === " ")) {
    endIndex--;
  }

  return str.slice(0, endIndex) + replacement;
}

/* eslint-enable no-redeclare */
